import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import InfinityCaseDetail from "../../components/admin/InfinityCaseDetail";
import DateRangeFilter from "../../components/admin/DateRangeFilter";
import ShipStationLogDialog from "../../components/admin/ShipStationLogDialog";
import { buildCustomerLoginUrl } from "../../utils/customerLink";
import {
  Package,
  Truck,
  CheckCircle2,
  Clock,
  XCircle,
  Search,
  Eye,
  RefreshCw,
  Mail,
  MapPin,
  DollarSign,
  Store,
  Calendar,
  Box,
  Send,
  ExternalLink,
  Loader2,
  Phone,
  CreditCard,
  User,
  Heart,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Hash,
  FileText,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sales channel separation (PMv3). Legacy orders with no `channel` show as Edisplay.
const CHANNEL_LABEL = { edisplay: "e-Displayroom", infinity: "Infinity Touch", funeral: "Funeral Store" };
const CHANNEL_BADGE = {
  edisplay: "bg-slate-100 text-slate-700",
  infinity: "bg-blue-100 text-blue-700",
  funeral: "bg-rose-100 text-rose-700",
};

// Who placed the order. `placed_by` defaults to 'customer' on the schema, so
// legacy and e-Displayroom orders (which have no funeral-home flow) read as
// Customer — which is correct.
const PLACED_BY_LABEL = { funeral_home: "Funeral Home", customer: "Customer" };
const PLACED_BY_BADGE = {
  funeral_home: "bg-amber-100 text-amber-700",
  customer: "bg-teal-100 text-teal-700",
};

const statusConfig = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  confirmed: { label: "Confirmed", color: "bg-blue-100 text-blue-800", icon: CheckCircle2 },
  processing: { label: "Processing", color: "bg-purple-100 text-purple-800", icon: Package },
  shipped: { label: "Shipped", color: "bg-primary/10 text-primary", icon: Truck },
  delivered: { label: "Delivered", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800", icon: XCircle }
};

const paymentStatusConfig = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800" },
  paid: { label: "Paid", color: "bg-green-100 text-green-800" },
  failed: { label: "Failed", color: "bg-red-100 text-red-800" },
  refunded: { label: "Refunded", color: "bg-slate-100 text-slate-800" }
};

const fulfillmentStatusConfig = {
  unfulfilled: { label: "Unfulfilled", color: "bg-yellow-100 text-yellow-800" },
  processing: { label: "Processing", color: "bg-purple-100 text-purple-800" },
  shipped: { label: "Shipped", color: "bg-primary/10 text-primary" },
  delivered: { label: "Delivered", color: "bg-green-100 text-green-800" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800" }
};

const shipstationStatusConfig = {
  awaiting_shipment: { label: "Awaiting Shipment", color: "bg-amber-100 text-amber-800" },
  shipped: { label: "Shipped", color: "bg-blue-100 text-blue-800" },
  delivered: { label: "Delivered", color: "bg-green-100 text-green-800" }
};

const carriers = [
  { value: "usps", label: "USPS" },
  { value: "ups", label: "UPS" },
  { value: "fedex", label: "FedEx" },
  { value: "dhl", label: "DHL" },
  { value: "other", label: "Other" }
];

// `role` is "ops" for an ops user (operations login): same list and status updates as an
// admin, but no Infinity email-order actions in the case popup.
export default function AdminOrders({ role } = {}) {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, processing: 0, shipped: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterStore, setFilterStore] = useState("all");
  const [filterChannel, setFilterChannel] = useState("all"); // all, edisplay, infinity, funeral
  const [filterProductSource, setFilterProductSource] = useState("all"); // all, admin, reseller
  // Deliberately defaults to "paid" so the list opens on real, payment-confirmed
  // orders rather than abandoned/failed checkouts (overrides the "all" default
  // TR-26 item 5 introduced). Failed/pending orders are still one filter click
  // away via the Payment dropdown — they're filtered by default, not hidden.
  const [filterPaymentStatus, setFilterPaymentStatus] = useState("paid");
  const [searchTerm, setSearchTerm] = useState("");
  const [date, setDate] = useState({
    from: new Date(new Date().getFullYear(), 0, 1),
    to: new Date(new Date().getFullYear(), 11, 31),
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [uniqueStores, setUniqueStores] = useState([]);

  // Sorting state
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // Modal state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [caseModal, setCaseModal] = useState(null); // { recordId, focusOrderId } for IT case view
  const [showDetails, setShowDetails] = useState(false);
  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const [showShipstationLog, setShowShipstationLog] = useState(false);

  // Update form state
  const [newStatus, setNewStatus] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [carrier, setCarrier] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [updating, setUpdating] = useState(false);
  const [simulatingShip, setSimulatingShip] = useState(false);
  const [simulatingDelivery, setSimulatingDelivery] = useState(false);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {
        ...(filterStatus !== "all" && { status: filterStatus }),
        ...(filterPaymentStatus !== "all" && { payment_status: filterPaymentStatus }),
        ...(filterStore !== "all" && { store_subdomain: filterStore }),
        ...(filterChannel !== "all" && { channel: filterChannel }),
        ...(filterProductSource !== "all" && { productSource: filterProductSource }),
        ...(searchTerm && { search: searchTerm }),
        sortField,
        sortDirection,
        page: currentPage,
        limit: itemsPerPage
      };

      if (date?.from) {
        // Start of day
        const startDay = new Date(date.from);
        startDay.setHours(0, 0, 0, 0);
        params.startDate = startDay.toISOString();
      }
      if (date?.to) {
        // End of day
        const endDay = new Date(date.to);
        endDay.setHours(23, 59, 59, 999);
        params.endDate = endDay.toISOString();
      }

      const response = await axios.get(`${API}/admin/orders`, { params });

      const data = response.data;
      if (Array.isArray(data)) {
        setOrders(data);
        setStats({
          total: data.length,
          pending: data.filter(o => o.status === 'pending').length,
          processing: data.filter(o => o.status === 'processing').length,
          shipped: data.filter(o => o.status === 'shipped').length
        });
      } else {
        setOrders(data.orders || []);
        setStats(data.stats || { total: 0, pending: 0, processing: 0, shipped: 0 });
        if (data.pagination) {
          setTotalItems(data.pagination.total);
          setTotalPages(data.pagination.pages);
          if (currentPage > data.pagination.pages && data.pagination.pages > 0) setCurrentPage(1);
        }
        if (data.uniqueStores) {
          setUniqueStores(data.uniqueStores);
        }
      }
    } catch (error) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchOrders();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [filterStatus, filterPaymentStatus, filterStore, filterChannel, filterProductSource, date, searchTerm, sortField, sortDirection, currentPage, itemsPerPage]);

  const handleViewDetails = async (order) => {
    // Infinity Touch orders open the case-centric view (case → many orders),
    // auto-scrolled to this order. Falls back to the standard modal otherwise.
    if (order.channel === 'infinity' && order.memorial_record_id) {
      setCaseModal({ recordId: order.memorial_record_id, focusOrderId: order.order_id });
      return;
    }
    setSelectedOrder(order);
    setShowDetails(true);
    try {
      const res = await axios.get(`${API}/admin/orders/${order.order_id}`);
      setSelectedOrder((prev) => (prev && prev.order_id === order.order_id ? { ...prev, ...res.data } : prev));
    } catch (e) {
      // keep the list-row data on failure
    }
  };

  const handleOpenUpdateStatus = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setTrackingNumber(order.tracking_number || "");
    setCarrier(order.carrier || "");
    setAdminNotes(order.admin_notes || "");
    setShowUpdateStatus(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder) return;

    setUpdating(true);
    try {
      await axios.put(`${API}/admin/orders/${selectedOrder.order_id}/status`, {
        status: newStatus,
        tracking_number: trackingNumber || null,
        carrier: carrier || null,
        admin_notes: adminNotes || null
      });

      toast.success(`Order ${selectedOrder.order_id} updated to ${newStatus}`);
      setShowUpdateStatus(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update order");
    } finally {
      setUpdating(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDirection === "asc"
      ? <ArrowUp className="w-3 h-3 ml-1 text-primary" />
      : <ArrowDown className="w-3 h-3 ml-1 text-primary" />;
  };

  const filteredOrders = orders; // Backend handles filtering now

  // Reset to first page when filtering/sorting changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStore, filterChannel, filterProductSource, filterPaymentStatus, filterStatus, searchTerm, sortField, sortDirection, itemsPerPage]);

  const paginatedOrders = filteredOrders; // Backend handles pagination now

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const handleSimulateShip = async (orderId, carrierCode = "usps") => {
    setSimulatingShip(true);
    try {
      const response = await axios.post(`${API}/shipstation/simulate-ship/${orderId}?carrier=${carrierCode}`);
      toast.success(`Order shipped via ${carrierCode.toUpperCase()}! Tracking: ${response.data.tracking_number}`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to simulate shipping");
    } finally {
      setSimulatingShip(false);
    }
  };

  const handleSimulateDelivery = async (orderId) => {
    setSimulatingDelivery(true);
    try {
      await axios.post(`${API}/shipstation/simulate-delivery/${orderId}`);
      toast.success("Order marked as delivered!");
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to simulate delivery");
    } finally {
      setSimulatingDelivery(false);
    }
  };

  const handlePushToShipStation = async (orderId) => {
    try {
      const response = await axios.post(`${API}/shipstation/push-order/${orderId}`);
      toast.success(response.data.message);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to push to ShipStation");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Order Management
          </h1>
          <p className="text-slate-500">Manage and fulfill customer orders</p>
        </div>
        <Button onClick={fetchOrders} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-elevated p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
              <Box className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-sm text-slate-500">Total Orders</p>
            </div>
          </div>
        </div>
        <div className="card-elevated p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-sm text-slate-500">Pending</p>
            </div>
          </div>
        </div>
        <div className="card-elevated p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Package className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{stats.processing}</p>
              <p className="text-sm text-slate-500">Processing</p>
            </div>
          </div>
        </div>
        <div className="card-elevated p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Truck className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-primary">{stats.shipped}</p>
              <p className="text-sm text-slate-500">Shipped</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-elevated p-4">
        <div className="flex flex-wrap items-end gap-3">
          {/* Search Bar */}
          <div className="relative w-full sm:w-[240px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-end gap-3 flex-1">
            <div className="flex flex-col gap-1 w-[150px]">
              <Label className="text-[10px] text-slate-400 font-bold uppercase ml-1">Channel</Label>
              <Select value={filterChannel} onValueChange={setFilterChannel}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All Channels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Channels</SelectItem>
                  <SelectItem value="edisplay">e-Displayroom</SelectItem>
                  <SelectItem value="infinity">Infinity Touch</SelectItem>
                  <SelectItem value="funeral">Funeral Store</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1 w-[140px]">
              <Label className="text-[10px] text-slate-400 font-bold uppercase ml-1">Store</Label>
              <Select value={filterStore} onValueChange={setFilterStore}>
                <SelectTrigger className="w-full">
                  <Store className="w-4 h-4 mr-2 text-slate-400 hidden sm:block" />
                  <SelectValue placeholder="All Stores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  {/* Filters by subdomain (what the API matches on) but reads as the
                      funeral home's name. Tolerates the old bare-string shape. */}
                  {uniqueStores.map((store) => {
                    const subdomain = typeof store === "string" ? store : store.subdomain;
                    const label = typeof store === "string" ? store : (store.name || store.subdomain);
                    return <SelectItem key={subdomain} value={subdomain}>{label}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1 w-[140px]">
              <Label className="text-[10px] text-slate-400 font-bold uppercase ml-1">Payment</Label>
              <Select value={filterPaymentStatus} onValueChange={setFilterPaymentStatus}>
                <SelectTrigger className="w-full">
                  <CreditCard className="w-4 h-4 mr-2 text-slate-400 hidden sm:block" />
                  <SelectValue placeholder="Payment Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Payment</SelectItem>
                  <SelectItem value="paid">Paid Only</SelectItem>
                  <SelectItem value="pending">Pending Only</SelectItem>
                  <SelectItem value="failed">Failed Only</SelectItem>
                  <SelectItem value="refunded">Refunded Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1 w-[140px]">
              <Label className="text-[10px] text-slate-400 font-bold uppercase ml-1">Order Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1 w-[260px]">
              <Label className="text-[10px] text-slate-400 font-bold uppercase ml-1">Date Range</Label>
              <DateRangeFilter value={date} onChange={setDate} className="w-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card-elevated overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Loading orders...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No orders found</p>
            <p className="text-sm text-slate-400 mt-1">Orders will appear here after customers make purchases</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50/80 text-slate-500 font-medium text-xs uppercase tracking-widest border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort("created_at")}>
                    <span className="flex items-center">Order (Date) <SortIcon field="created_at" /></span>
                  </th>
                  <th className="px-4 py-3 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort("store_subdomain")}>
                    <span className="flex items-center">Store <SortIcon field="store_subdomain" /></span>
                  </th>
                  <th className="px-4 py-3 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort("channel")}>
                    <span className="flex items-center">Channel <SortIcon field="channel" /></span>
                  </th>
                  <th className="px-4 py-3 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort("customer_email")}>
                    <span className="flex items-center">Customer <SortIcon field="customer_email" /></span>
                  </th>
                  <th className="px-4 py-3 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort("placed_by")}>
                    <span className="flex items-center">Ordered By <SortIcon field="placed_by" /></span>
                  </th>
                  <th className="px-4 py-3 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort("total")}>
                    <span className="flex items-center">Total <SortIcon field="total" /></span>
                  </th>
                  <th className="px-4 py-3 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort("payment_status")}>
                    <span className="flex items-center">Payment <SortIcon field="payment_status" /></span>
                  </th>
                  <th className="px-4 py-3 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort("fulfillment_status")}>
                    <span className="flex items-center">Shipment <SortIcon field="fulfillment_status" /></span>
                  </th>
                  <th className="px-4 py-3 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort("status")}>
                    <span className="flex items-center">Status <SortIcon field="status" /></span>
                  </th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {paginatedOrders.map((order) => {
                  const StatusIcon = statusConfig[order.status]?.icon || Clock;
                  return (
                    <tr key={order.order_id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-mono font-bold text-slate-900 text-xs">
                          {order.order_number ? `#${order.order_number}` : `${order.order_id.slice(0, 15)}...`}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {formatShortDate(order.created_at)}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-slate-600 font-medium">
                          {order.store_id === "0" ? "Platform Main" : (order.store_name || order.store_subdomain || 'N/A')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`rounded-md px-1.5 py-0 border-none text-[10px] font-bold shadow-none ${CHANNEL_BADGE[order.channel] || "bg-slate-100 text-slate-700"}`}>
                          {CHANNEL_LABEL[order.channel] || "e-Displayroom"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium text-slate-900 truncate max-w-[150px]">{order.customer_name || 'Guest'}</p>
                        <p className="text-[10px] text-slate-400 truncate max-w-[150px]">{order.customer_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`rounded-md px-1.5 py-0 border-none text-[10px] font-bold shadow-none ${PLACED_BY_BADGE[order.placed_by] || PLACED_BY_BADGE.customer}`}>
                          {PLACED_BY_LABEL[order.placed_by] || PLACED_BY_LABEL.customer}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-bold text-slate-900">${(order.total_charged || order.total || 0).toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`rounded-md px-1.5 py-0 border-none text-[10px] font-bold shadow-none ${paymentStatusConfig[order.payment_status]?.color || "bg-slate-100 text-slate-700"}`}>
                          {paymentStatusConfig[order.payment_status]?.label || order.payment_status || 'N/A'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`rounded-md px-1.5 py-0 border-none text-[10px] font-bold shadow-none ${fulfillmentStatusConfig[order.fulfillment_status]?.color || "bg-slate-100 text-slate-700"}`}>
                          {fulfillmentStatusConfig[order.fulfillment_status]?.label || order.fulfillment_status || 'N/A'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`rounded-md px-1.5 py-0 border-none text-[10px] font-bold shadow-none ${statusConfig[order.status]?.color || "bg-slate-100"}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[order.status]?.label || order.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs rounded-lg"
                            onClick={() => handleViewDetails(order)}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            className="h-7 px-2 text-xs bg-primary hover:bg-primary/90 rounded-lg"
                            onClick={() => handleOpenUpdateStatus(order)}
                          >
                            Update
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && totalPages > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-4 border-t gap-4">
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <div>
                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of <span className="font-medium">{totalItems}</span> results
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-600">Rows per page:</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(val) => {
                    setItemsPerPage(Number(val));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                >
                  <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue placeholder={itemsPerPage.toString()} />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {[10, 15, 25, 50, 100].map((pageSize) => (
                      <SelectItem key={pageSize} value={pageSize.toString()}>
                        {pageSize}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>

              {/* Numbered Pagination Logic */}
              <div className="flex items-center mx-2 space-x-1 hidden sm:flex">
                {Array.from({ length: totalPages }).map((_, i) => {
                  const pageNum = i + 1;
                  // Show current page, first page, last page, and adjacent pages
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        className={cn(
                          "h-8 w-8 p-0",
                          currentPage === pageNum ? "bg-primary hover:bg-primary/90 text-white" : "text-slate-600 hover:bg-slate-100"
                        )}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  }

                  // Ellipses logic
                  if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                  }

                  return null;
                })}
              </div>
              {/* Mobile simplified view */}
              <div className="flex sm:hidden items-center px-3 text-sm font-medium text-slate-600">
                {currentPage} / {totalPages}
              </div>

              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Order {selectedOrder?.order_number ? `#${selectedOrder.order_number}` : selectedOrder?.order_id}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6">
              {/* Status & Total */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={`mt-1 ${statusConfig[selectedOrder.status]?.color}`}>
                    {statusConfig[selectedOrder.status]?.label}
                  </Badge>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-2xl font-bold text-slate-900">${(selectedOrder.total_charged || selectedOrder.total || 0).toFixed(2)}</p>
                </div>
              </div>

              {/* All Statuses */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Payment</p>
                  <Badge className={`rounded-md px-2 py-0.5 border-none text-xs font-bold ${paymentStatusConfig[selectedOrder.payment_status]?.color || "bg-slate-100 text-slate-700"}`}>
                    {paymentStatusConfig[selectedOrder.payment_status]?.label || selectedOrder.payment_status || 'N/A'}
                  </Badge>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Shipment</p>
                  <Badge className={`rounded-md px-2 py-0.5 border-none text-xs font-bold ${fulfillmentStatusConfig[selectedOrder.fulfillment_status]?.color || "bg-slate-100 text-slate-700"}`}>
                    {fulfillmentStatusConfig[selectedOrder.fulfillment_status]?.label || selectedOrder.fulfillment_status || 'N/A'}
                  </Badge>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Order</p>
                  <Badge className={`rounded-md px-2 py-0.5 border-none text-xs font-bold ${statusConfig[selectedOrder.status]?.color || "bg-slate-100 text-slate-700"}`}>
                    {statusConfig[selectedOrder.status]?.label || selectedOrder.status || 'N/A'}
                  </Badge>
                </div>
              </div>

              {/* Customer Info */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Customer Information</h4>
                <div className="space-y-2 text-sm">
                  {selectedOrder.customer_name && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{selectedOrder.customer_name}</span>
                      {selectedOrder.is_guest && (
                        <Badge className="bg-slate-100 text-slate-600 text-[10px] rounded-md ml-1">Guest</Badge>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span>{selectedOrder.customer_email}</span>
                  </div>
                  {selectedOrder.contact_email && selectedOrder.contact_email !== selectedOrder.customer_email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span>{selectedOrder.contact_email} (contact)</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Store className="w-4 h-4 text-slate-400" />
                    <span>{selectedOrder.store_name || selectedOrder.store_subdomain}</span>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              {selectedOrder.shipping_address && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    Shipping Address
                  </h4>
                  <div className="text-sm text-slate-700 space-y-0.5 p-4 bg-slate-50 rounded-xl">
                    {selectedOrder.shipping_address.name && <p className="font-bold text-slate-900">{selectedOrder.shipping_address.name}</p>}
                    {selectedOrder.shipping_address.line1 && <p>{selectedOrder.shipping_address.line1}</p>}
                    {selectedOrder.shipping_address.line2 && <p>{selectedOrder.shipping_address.line2}</p>}
                    <p>
                      {[selectedOrder.shipping_address.city, selectedOrder.shipping_address.state, selectedOrder.shipping_address.postal_code].filter(Boolean).join(', ')}
                    </p>
                    {selectedOrder.shipping_address.country && selectedOrder.shipping_address.country !== 'US' && (
                      <p>{selectedOrder.shipping_address.country}</p>
                    )}
                    {selectedOrder.shipping_address.phone && (
                      <p className="flex items-center gap-1.5 mt-2 text-slate-500">
                        <Phone className="w-3.5 h-3.5" /> {selectedOrder.shipping_address.phone}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Infinity Touch — Memorial / Print Record + family link */}
              {selectedOrder.channel === 'infinity' && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    Memorial / Print Record
                  </h4>
                  <div className="text-sm text-slate-700 space-y-2 p-4 bg-blue-50/50 rounded-xl">
                    {selectedOrder.customer_pin && (
                      <p className="text-xs">
                        Customer Link: <a href={buildCustomerLoginUrl(selectedOrder.order_number || selectedOrder.order_id, selectedOrder.customer_pin)} target="_blank" rel="noopener noreferrer" className="text-blue-700 break-all hover:underline">{buildCustomerLoginUrl(selectedOrder.order_number || selectedOrder.order_id, selectedOrder.customer_pin)}</a>
                        <span className="ml-2">(PIN <span className="font-bold">{selectedOrder.customer_pin}</span>)</span>
                      </p>
                    )}
                    {selectedOrder.memorial_record ? (
                      <>
                        <p><span className="text-slate-500">Case:</span> <span className="font-medium">{selectedOrder.memorial_record.case_number}</span> · <span className="capitalize">{selectedOrder.memorial_record.print_type}</span></p>
                        {selectedOrder.memorial_record.subject && (
                          <p>
                            <span className="text-slate-500">Subject:</span>{' '}
                            {[selectedOrder.memorial_record.subject.first_name, selectedOrder.memorial_record.subject.last_name].filter(Boolean).join(' ')}
                            {selectedOrder.memorial_record.subject.gender ? ` · ${selectedOrder.memorial_record.subject.gender}` : ''}
                          </p>
                        )}
                        {(selectedOrder.memorial_record.captures || []).length > 0 && (
                          <div>
                            <p className="text-slate-500 mb-1">Captured prints:</p>
                            <div className="flex flex-wrap gap-3">
                              {selectedOrder.memorial_record.captures.map((cap, i) => {
                                const url = cap.image_url?.startsWith('http') ? cap.image_url : `${process.env.REACT_APP_BACKEND_URL}${cap.image_url || ''}`;
                                return cap.image_url ? (
                                  <a key={i} href={url} target="_blank" rel="noopener noreferrer" download className="flex flex-col items-center text-[10px] text-blue-700 hover:underline">
                                    <img src={url} alt={`capture-${i}`} className="w-16 h-16 object-cover rounded-lg border" />
                                    <span className="mt-1">{cap.target?.finger || cap.target?.target || cap.target?.kind || 'print'} ↓</span>
                                  </a>
                                ) : null;
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-slate-400 text-xs">No linked memorial record.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Delivery Info (Funeral specific) */}
              {(selectedOrder.deceased_name || selectedOrder.delivery_date || selectedOrder.delivery_time) && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-rose-500" />
                    Deceased Name
                  </h4>
                  <div className="text-sm text-slate-700 space-y-1 p-4 bg-slate-50 rounded-xl">
                    {selectedOrder.deceased_name && (
                      <p className="font-bold text-slate-900">In Memory of: {selectedOrder.deceased_name}</p>
                    )}
                    {selectedOrder.delivery_date && (
                      <p className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" /> {formatShortDate(selectedOrder.delivery_date)}
                      </p>
                    )}
                    {selectedOrder.delivery_time && (
                      <p className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-slate-400" /> {selectedOrder.delivery_time}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Order Items */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Order Items</h4>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} className="w-16 h-16 object-contain bg-white rounded-lg border border-slate-100 p-1" />
                      ) : (
                        <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center">
                          <Package className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-slate-900">{item.name}</p>
                          {item.product_type === 'custom' && (
                            <Badge variant="outline" className="text-[10px] h-4 px-1 py-0 flex items-center border-amber-200 bg-amber-50 text-amber-600">Reseller Products</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">Qty: {item.quantity} × ${item.display_price?.toFixed(2)}</p>

                        {/* Customizations display */}
                        {item.customizations && (Array.isArray(item.customizations) ? item.customizations.length > 0 : Object.keys(item.customizations).length > 0) && (
                          <div className="mt-2 space-y-1.5 pl-2 border-l-2 border-slate-100 py-0.5">
                            {Array.isArray(item.customizations) ? (
                              item.customizations.map((cust) => (
                                <div key={cust.id} className="text-[10px]">
                                  <div className="flex justify-between font-bold text-slate-600">
                                    <span>{cust.name}</span>
                                    <span className="text-indigo-600">+${cust.total.toFixed(2)}</span>
                                  </div>
                                  {cust.fields.map((f, i) => (
                                    <p key={i} className="text-slate-400 italic leading-tight">
                                      {f.label}: {f.value}
                                    </p>
                                  ))}
                                </div>
                              ))
                            ) : (
                              Object.entries(item.customizations).map(([label, val]) => (
                                <p key={label} className="text-[10px] text-slate-400 italic">
                                  <span className="font-medium mr-1 text-slate-500">{label}:</span>
                                  {val && typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))
                                    ? new Date(val).toLocaleDateString()
                                    : val === true ? 'Yes' : val}
                                </p>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                      <p className="font-semibold text-slate-900">${(item.display_price * item.quantity)?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="card-elevated p-4 bg-slate-50 border-none shadow-none mt-6">
                <h4 className="font-semibold text-slate-900 mb-3 border-b border-slate-200 pb-2">Pricing Breakdown</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center py-1">
                    <span className="text-slate-500">Subtotal</span>
                    <span className="font-semibold text-slate-900">${(selectedOrder.subtotal || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-slate-500">Shipping</span>
                    <span className="font-semibold text-primary">${(selectedOrder.shipping || selectedOrder.shipping_cost || 0).toFixed(2)}</span>
                  </div>
                  {selectedOrder.shipping_method && (
                    <div className="flex justify-between items-center py-1">
                      <span className="text-slate-500">Shipping Method</span>
                      <span className="text-slate-700 font-medium">{selectedOrder.shipping_method}</span>
                    </div>
                  )}
                  <div className="border-t border-slate-200 my-2"></div>

                  {selectedOrder.platform_revenue > 0 && (
                    <div className="flex justify-between items-center bg-white/50 p-2 rounded-lg group hover:bg-white transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                        <span className="text-slate-600">Platform Revenue (Net)</span>
                      </div>
                      <span className="text-green-600 font-bold font-mono">${(selectedOrder.platform_revenue || 0).toFixed(2)}</span>
                    </div>
                  )}

                  {selectedOrder.reseller_commission > 0 && (
                    <div className="flex justify-between items-center bg-white/50 p-2 rounded-lg group hover:bg-white transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                        <span className="text-slate-600">Reseller Commission</span>
                      </div>
                      <span className="text-amber-600 font-bold font-mono">${(selectedOrder.reseller_commission || 0).toFixed(2)}</span>
                    </div>
                  )}

                  {selectedOrder.distributor_commission > 0 && (
                    <div className="flex justify-between items-center bg-white/50 p-2 rounded-lg group hover:bg-white transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                        <span className="text-slate-600">Distributor Commission</span>
                      </div>
                      <span className="text-blue-600 font-bold font-mono">${(selectedOrder.distributor_commission || 0).toFixed(2)}</span>
                    </div>
                  )}

                  {(selectedOrder.shipping > 0 || selectedOrder.shipping_cost > 0) && (
                    <div className="flex justify-between items-center bg-white/50 p-2 rounded-lg group hover:bg-white transition-colors">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        <span className="text-slate-600">Shipping Collected (Platform)</span>
                      </div>
                      <span className="text-primary font-bold font-mono">${(selectedOrder.shipping || selectedOrder.shipping_cost || 0).toFixed(2)}</span>
                    </div>
                  )}

                  <div className="border-t border-slate-200 pt-3 flex justify-between items-center font-bold text-base mt-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-slate-900"></div>
                      <span className="text-slate-900 uppercase text-xs tracking-wider">Total Charged</span>
                    </div>
                    <span className="text-primary text-xl font-black">${(selectedOrder.total_charged || selectedOrder.total || 0).toFixed(2)}</span>
                  </div>

                </div>
              </div>

              {/* Shipping / Tracking Info — always rendered, because the ShipStation log
                  is most useful precisely when no tracking arrived (unknown order number,
                  internal error). Those callbacks are acknowledged with 200, so this popup
                  is the only place they show up. */}
              {(
                <div>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <h4 className="font-semibold text-slate-900">Tracking Information</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowShipstationLog(true)}
                    >
                      <Truck className="w-3.5 h-3.5 mr-1.5" />
                      ShipStation Log
                    </Button>
                  </div>
                  <div className="space-y-2 text-sm">
                    {!selectedOrder.tracking_number && !selectedOrder.carrier && !selectedOrder.shipstation_order_id && (
                      <p className="text-slate-400">No tracking recorded yet.</p>
                    )}
                    {selectedOrder.shipstation_order_id && (
                      <div className="p-3 bg-blue-50 rounded-lg mb-3">
                        <div className="flex items-center gap-2 text-blue-700 font-medium">
                          <Send className="w-4 h-4" />
                          ShipStation Order: {selectedOrder.shipstation_order_id}
                        </div>
                        <div className="text-blue-600 text-xs mt-1">
                          Status: {shipstationStatusConfig[selectedOrder.shipstation_status]?.label || selectedOrder.shipstation_status}
                        </div>
                      </div>
                    )}
                    {selectedOrder.carrier && (
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-slate-400" />
                        <span>Carrier: {selectedOrder.carrier.toUpperCase()}</span>
                      </div>
                    )}
                    {selectedOrder.tracking_number && (
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-slate-400" />
                        <span>Tracking: {selectedOrder.tracking_number}</span>
                        <a
                          href={selectedOrder.tracking_url || `https://www.google.com/search?q=${selectedOrder.carrier}+tracking+${selectedOrder.tracking_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}

                    {/* Every distinct shipment ShipStation told us about. Only worth showing
                        once there's more than one — split shipments, or a voided label that
                        was recreated with new tracking. */}
                    {selectedOrder.shipments?.length > 1 && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="text-xs font-semibold text-slate-600 mb-2">
                          All shipments ({selectedOrder.shipments.length})
                        </div>
                        <div className="space-y-1.5">
                          {selectedOrder.shipments.map((s, i) => (
                            <div key={s.tracking_number || i} className="text-xs text-slate-600 flex flex-wrap gap-x-2">
                              <span className="font-medium text-slate-800">{s.tracking_number}</span>
                              {s.carrier && <span>· {String(s.carrier).toUpperCase()}</span>}
                              {s.service && <span>· {s.service}</span>}
                              {s.ship_date && <span>· {formatDate(s.ship_date)}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedOrder.admin_notes && (
                    <div className="mt-4">
                      <h4 className="font-semibold text-slate-900 mb-2">Internal Notes</h4>
                      <pre className="text-xs bg-slate-50 border rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words text-slate-700 max-h-64 overflow-y-auto">
                        {selectedOrder.admin_notes}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Timestamps */}
              <div className="text-xs text-slate-400 space-y-1">
                <p>Created: {formatDate(selectedOrder.created_at)}</p>
                {selectedOrder.paid_at && <p>Paid: {formatDate(selectedOrder.paid_at)}</p>}
                {selectedOrder.shipped_at && <p>Shipped: {formatDate(selectedOrder.shipped_at)}</p>}
                {selectedOrder.delivered_at && <p>Delivered: {formatDate(selectedOrder.delivered_at)}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Update Status Modal */}
      <Dialog open={showUpdateStatus} onOpenChange={setShowUpdateStatus}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Order Status</DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-mono font-bold">{selectedOrder.order_id}</p>
                <p className="text-sm text-slate-500">{selectedOrder.items?.length} items • ${selectedOrder.total?.toFixed(2)}</p>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(newStatus === "shipped" || newStatus === "delivered") && (
                <>
                  <div className="space-y-2">
                    <Label>Carrier</Label>
                    <Select value={carrier} onValueChange={setCarrier}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select carrier" />
                      </SelectTrigger>
                      <SelectContent>
                        {carriers.map((c) => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tracking Number</Label>
                    <Input
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      placeholder="Enter tracking number"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label>Admin Notes (Optional)</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes about this order..."
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowUpdateStatus(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={handleUpdateStatus}
                  disabled={updating}
                >
                  {updating ? "Updating..." : "Update Order"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Infinity Touch — case-centric order view */}
      {caseModal && (
        <InfinityCaseDetail
          open={!!caseModal}
          onClose={() => { setCaseModal(null); fetchOrders(); }}
          memorialRecordId={caseModal.recordId}
          focusOrderId={caseModal.focusOrderId}
          allowEmailOrders={role !== "ops"}
        />
      )}

      {/* Inbound ShipStation shipnotify callbacks for the selected order */}
      {selectedOrder && (
        <ShipStationLogDialog
          open={showShipstationLog}
          onClose={() => setShowShipstationLog(false)}
          orderId={selectedOrder.order_id}
          orderNumber={selectedOrder.order_number}
        />
      )}
    </div >
  );
}
